import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { mailboxService, mailboxesQueryKey } from "../services/mailbox";
import { defaultMailbox } from "../utils/mailboxScope";
import { getStoredMailboxHash, persistMailboxHash } from "../utils/mailboxScopeStorage";

const MailboxScopeContext = createContext(null);

export function MailboxScopeProvider({ enabled, children }) {
  const { data, isLoading } = useQuery({
    queryKey: mailboxesQueryKey,
    queryFn: mailboxService.list,
    enabled,
  });
  const mailboxes = useMemo(() => (data?.results ?? []).filter((m) => m.connected), [data]);
  const [mailboxHash, setMailboxHashState] = useState("");

  useEffect(() => {
    if (!enabled || isLoading) {
      return;
    }
    if (!mailboxes.length) {
      setMailboxHashState("");
      persistMailboxHash("");
      return;
    }
    setMailboxHashState((current) => {
      const stored = getStoredMailboxHash();
      if (stored && mailboxes.some((m) => m.hash === stored)) {
        return stored;
      }
      if (current && mailboxes.some((m) => m.hash === current)) {
        return current;
      }
      const chosen = defaultMailbox(mailboxes)?.hash ?? "";
      if (chosen) {
        persistMailboxHash(chosen);
      }
      return chosen;
    });
  }, [enabled, isLoading, mailboxes]);

  const setMailboxHash = useCallback((hash) => {
    persistMailboxHash(hash);
    setMailboxHashState(hash);
  }, []);

  const value = useMemo(
    () => ({
      mailboxes,
      mailboxesLoading: isLoading,
      mailboxHash,
      setMailboxHash,
    }),
    [mailboxes, isLoading, mailboxHash, setMailboxHash],
  );

  return (
    <MailboxScopeContext.Provider value={enabled ? value : null}>
      {children}
    </MailboxScopeContext.Provider>
  );
}

export function useMailboxScope() {
  return useContext(MailboxScopeContext);
}
